import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Table,
  Typography,
  Space,
  Button,
  Select,
  DatePicker,
  Tag,
  Tooltip,
  notification,
  Empty,
} from 'antd';
import { ReloadOutlined, AuditOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { getAuditLog } from '../api/client';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const ACTION_COLORS = {
  login:           'blue',
  sg_rule_added:   'green',
  sg_rule_updated: 'gold',
  sg_rule_deleted: 'red',
};

const ACTION_LABELS = {
  login:           'Login',
  sg_rule_added:   'Rule Added',
  sg_rule_updated: 'Rule Updated',
  sg_rule_deleted: 'Rule Deleted',
};

function ActionTag({ action }) {
  return (
    <Tag color={ACTION_COLORS[action] || 'default'}>
      {ACTION_LABELS[action] || action}
    </Tag>
  );
}

function DetailsCell({ details, action }) {
  if (!details || Object.keys(details).length === 0) return <Text type="secondary">—</Text>;

  const parts = [];
  if (details.cidr_ip)       parts.push(`CIDR: ${details.cidr_ip}`);
  if (details.protocol)      parts.push(`Proto: ${details.protocol}`);
  if (details.from_port != null && details.to_port != null) {
    parts.push(details.from_port === details.to_port
      ? `Port: ${details.from_port}`
      : `Ports: ${details.from_port}–${details.to_port}`);
  }
  if (details.old_description != null && details.new_description != null) {
    parts.push(`Desc: "${details.old_description}" → "${details.new_description}"`);
  } else if (details.new_description != null) {
    parts.push(`Desc: "${details.new_description}"`);
  } else if (details.description) {
    parts.push(`Desc: "${details.description}"`);
  }
  if (details.rule_id)       parts.push(`Rule: ${details.rule_id}`);
  if (details.sg_name)       parts.push(details.sg_name);

  return (
    <Tooltip title={JSON.stringify(details, null, 2)}>
      <Text style={{ cursor: 'help', fontSize: 12 }}>
        {parts.length > 0 ? parts.join(' · ') : JSON.stringify(details)}
      </Text>
    </Tooltip>
  );
}

function ResizableTitle({ onResize, width, ...restProps }) {
  const thRef = useRef(null);

  const handleMouseDown = (e) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();

    const startX = e.clientX;
    const startWidth = width;

    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';

    const onMouseMove = (e) => {
      const newWidth = Math.max(40, Math.min(1000, startWidth + e.clientX - startX));
      if (thRef.current) {
        thRef.current.style.width = `${newWidth}px`;
        thRef.current.style.minWidth = `${newWidth}px`;
        thRef.current.style.maxWidth = `${newWidth}px`;
      }
    };

    const onMouseUp = (e) => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
      const newWidth = Math.max(40, Math.min(1000, startWidth + e.clientX - startX));
      onResize(newWidth);
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  };

  if (!width) return <th {...restProps} />;

  return (
    <th
      {...restProps}
      ref={thRef}
      style={{ ...restProps.style, position: 'relative', overflow: 'visible' }}
    >
      {restProps.children}
      <span
        onMouseDown={handleMouseDown}
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'absolute',
          right: -3,
          top: 0,
          height: '100%',
          width: 6,
          cursor: 'col-resize',
          zIndex: 1,
        }}
      />
    </th>
  );
}

const DEFAULT_COLUMNS = [
  {
    title: 'Timestamp',
    dataIndex: 'timestamp',
    key: 'timestamp',
    width: 200,
    ellipsis: true,
    render: (ts) => (
      <Text style={{ fontSize: 12 }}>
        {ts ? dayjs(ts).format('YYYY-MM-DD HH:mm:ss') : '—'}
      </Text>
    ),
  },
  {
    title: 'User',
    dataIndex: 'user',
    key: 'user',
    width: 150,
    ellipsis: true,
    render: (u) => <Text strong>{u || '—'}</Text>,
  },
  {
    title: 'Action',
    dataIndex: 'action',
    key: 'action',
    width: 140,
    ellipsis: true,
    render: (action) => <ActionTag action={action} />,
  },
  {
    title: 'Resource',
    dataIndex: 'resource',
    key: 'resource',
    width: 200,
    ellipsis: true,
    render: (r) => <Text code style={{ fontSize: 12 }}>{r || '—'}</Text>,
  },
  {
    title: 'Details',
    dataIndex: 'details',
    key: 'details',
    width: 400,
    ellipsis: true,
    render: (details, record) => <DetailsCell details={details} action={record.action} />,
  },
];

export default function Audit() {
  const [events, setEvents]       = useState([]);
  const [loading, setLoading]     = useState(false);
  const [actionFilter, setAction] = useState(null);
  const [dateRange, setDateRange] = useState(null);
  const [columns, setColumns]     = useState(DEFAULT_COLUMNS);

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getAuditLog(1000);
      setEvents(data.events || []);
    } catch (err) {
      notification.error({
        message: 'Failed to load audit log',
        description: err.response?.data?.message ?? err.message ?? 'Could not fetch audit events.',
        duration: 0,
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  const handleResize = (index) => (newWidth) => {
    setColumns((cols) => {
      const next = [...cols];
      next[index] = { ...next[index], width: newWidth };
      return next;
    });
  };

  const resizableColumns = columns.map((col, index) => ({
    ...col,
    onHeaderCell: (column) => ({
      width: column.width,
      onResize: handleResize(index),
    }),
  }));

  const filtered = events.filter((e) => {
    if (actionFilter && e.action !== actionFilter) return false;
    if (dateRange) {
      const ts = dayjs(e.timestamp);
      if (ts.isBefore(dateRange[0].startOf('day'))) return false;
      if (ts.isAfter(dateRange[1].endOf('day')))   return false;
    }
    return true;
  });

  const actionOptions = [
    { value: 'login',           label: 'Login' },
    { value: 'sg_rule_added',   label: 'Rule Added' },
    { value: 'sg_rule_updated', label: 'Rule Updated' },
    { value: 'sg_rule_deleted', label: 'Rule Deleted' },
  ];

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Space align="center" style={{ justifyContent: 'space-between', width: '100%' }}>
        <Space align="center">
          <AuditOutlined style={{ fontSize: 20 }} />
          <Title level={4} style={{ margin: 0 }}>Audit Log</Title>
          <Text type="secondary">({filtered.length} events)</Text>
        </Space>
        <Button icon={<ReloadOutlined />} onClick={fetchEvents} loading={loading}>
          Refresh
        </Button>
      </Space>

      <Space wrap>
        <Select
          allowClear
          placeholder="Filter by action"
          style={{ width: 180 }}
          options={actionOptions}
          value={actionFilter}
          onChange={setAction}
        />
        <RangePicker
          value={dateRange}
          onChange={setDateRange}
          allowClear
        />
      </Space>

      <Table
        rowKey={(r) => r.sk || `${r.timestamp}-${Math.random()}`}
        columns={resizableColumns}
        dataSource={filtered}
        loading={loading}
        size="small"
        tableLayout="fixed"
        components={{ header: { cell: ResizableTitle } }}
        locale={{ emptyText: <Empty description="No audit events found" /> }}
        pagination={{
          defaultPageSize: 50,
          showSizeChanger: true,
          pageSizeOptions: ['20', '50', '100', '500'],
          showTotal: (total) => `${total} events`,
        }}
      />
    </Space>
  );
}
